import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  ArrowLeft, ToggleRight, Anchor, FileText, Mic, Settings, 
  Coins, Shield, Loader2, ChevronDown, ChevronRight, Users, 
  UserCog, RefreshCw, Zap, PlayCircle, TrendingUp, BookOpen, Lightbulb, EyeOff,
  Globe, Eye, PenTool, Target, Notebook, AlertTriangle, MessageCircle, Video
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICON_MAP = {
  anchor: Anchor,
  'file-text': FileText,
  mic: Mic,
  settings: Settings,
  coins: Coins,
  shield: Shield,
  'play-circle': PlayCircle,
  'trending-up': TrendingUp,
  'book-open': BookOpen,
  lightbulb: Lightbulb,
  zap: Zap,
  'message-circle': MessageCircle,
  search: Target
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' },
  coming_soon: { label: 'Coming Soon', color: 'bg-yellow-100 text-yellow-800', dotColor: 'bg-yellow-500' },
  hidden: { label: 'Hidden', color: 'bg-gray-100 text-gray-600', dotColor: 'bg-gray-400', icon: 'eye-off' },
  disabled: { label: 'Disabled', color: 'bg-red-100 text-red-600', dotColor: 'bg-red-400' }
};

const VISIBILITY_CONFIG = {
  everyone: { label: 'Everyone', color: 'bg-blue-100 text-blue-800', description: 'Visible to guests and logged-in users' },
  logged_in: { label: 'Logged-in Only', color: 'bg-purple-100 text-purple-800', description: 'Hidden from guests, visible after login' }
};

// Modules that should NOT have hidden option (always visible to users)
const NON_HIDEABLE_MODULES = ['coin_rewards', 'two_factor_auth'];

const CATEGORY_ORDER = ['exams', 'notebook', 'insights', 'live_classes', 'features', 'security'];
const CATEGORY_LABELS = {
  exams: 'Exam Modules',
  notebook: 'Study Notebook',
  insights: 'Exam Insights',
  live_classes: 'Live Classes',
  features: 'Features',
  security: 'Security'
};

const ModuleActivationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['exams']);
  const [expandedModules, setExpandedModules] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Guest visibility states
  const [visibilityExpanded, setVisibilityExpanded] = useState(false);
  const [visibilitySettings, setVisibilitySettings] = useState({
    oral: 'logged_in',
    written: 'logged_in',
    practice_sessions: 'logged_in',
    progress_tracker: 'logged_in',
    notebook: 'logged_in',
    exam_insights: 'logged_in',
    live_classes: 'logged_in'
  });
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);

  const isFullAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchModules();
    fetchVisibilitySettings();
  }, []);

  const fetchVisibilitySettings = async () => {
    try {
      setVisibilityLoading(true);
      const response = await axios.get(`${API}/admin-tools/modules/visibility-summary`);
      setVisibilitySettings(response.data.visibility || {});
    } catch (error) {
      console.error('Failed to fetch visibility settings:', error);
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleVisibilitySave = async () => {
    try {
      setVisibilitySaving(true);
      await axios.put(`${API}/admin-tools/visibility/bulk`, visibilitySettings);
      toast.success('Guest visibility settings updated');
      fetchModules(); // Refresh modules to show updated visibility
    } catch (error) {
      console.error('Failed to update visibility settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update visibility settings');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const fetchModules = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const response = await axios.get(`${API}/admin-tools/modules`);
      setModules(response.data.modules || []);
      
      // Auto-expand modules that have active children
      const parentKeys = (response.data.modules || [])
        .filter(m => m.category === 'exams')
        .map(m => m.module_key);
      setExpandedModules(parentKeys);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      const errorMsg = error.response?.status === 403 
        ? 'Access denied. Please ensure you are logged in as an admin.'
        : error.response?.status === 401
        ? 'Authentication required. Please log in.'
        : 'Failed to load modules. Please try again.';
      setFetchError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (moduleKey, field, currentValue) => {
    const newStatus = currentValue === 'active' ? 'coming_soon' : 'active';
    
    setUpdating(`${moduleKey}-${field}`);
    try {
      await axios.put(`${API}/admin-tools/modules/${moduleKey}`, { 
        [field]: newStatus 
      });
      toast.success(`Module updated successfully`);
      fetchModules();
    } catch (error) {
      console.error('Failed to update module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setUpdating(null);
    }
  };

  const handleStatusCycle = async (moduleKey, field, currentStatus) => {
    const statusCycle = ['active', 'coming_soon', 'disabled'];
    const currentIndex = statusCycle.indexOf(currentStatus);
    const newStatus = statusCycle[(currentIndex + 1) % statusCycle.length];
    
    setUpdating(`${moduleKey}-${field}`);
    try {
      await axios.put(`${API}/admin-tools/modules/${moduleKey}`, { 
        [field]: newStatus 
      });
      toast.success(`Status changed to "${STATUS_CONFIG[newStatus].label}"`);
      fetchModules();
    } catch (error) {
      console.error('Failed to update module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setUpdating(null);
    }
  };

  const handleUserStatusChange = async (moduleKey, newStatus) => {
    setUpdating(`${moduleKey}-status_user`);
    try {
      await axios.put(`${API}/admin-tools/modules/${moduleKey}`, { 
        status_user: newStatus 
      });
      toast.success(`User status changed to "${STATUS_CONFIG[newStatus].label}"`);
      fetchModules();
    } catch (error) {
      console.error('Failed to update module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setUpdating(null);
    }
  };

  const handleVisibilityChange = async (moduleKey, newVisibility) => {
    setUpdating(`${moduleKey}-visibility`);
    try {
      await axios.put(`${API}/admin-tools/modules/${moduleKey}`, { 
        visibility: newVisibility 
      });
      toast.success(`Visibility changed to "${VISIBILITY_CONFIG[newVisibility].label}"`);
      fetchModules();
    } catch (error) {
      console.error('Failed to update module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setUpdating(null);
    }
  };

  const handleMigrateVisibility = async () => {
    try {
      setResetting(true);
      const response = await axios.post(`${API}/admin-tools/modules/migrate-visibility`);
      toast.success(response.data.message);
      fetchModules();
    } catch (error) {
      console.error('Failed to migrate visibility:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate visibility');
    } finally {
      setResetting(false);
    }
  };

  const handleReset = async () => {
    setShowResetConfirm(false);
    setResetting(true);
    try {
      await axios.post(`${API}/admin-tools/modules/reset`);
      toast.success('All modules reset to defaults');
      fetchModules();
    } catch (error) {
      console.error('Failed to reset modules:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset modules');
    } finally {
      setResetting(false);
    }
  };

  // Count modules that will change on reset
  const getResetImpact = () => {
    let activeUserModules = 0;
    let hiddenModules = 0;
    let everyoneVisibility = 0;
    
    const countModules = (moduleList) => {
      moduleList.forEach(mod => {
        if (mod.status_user === 'active') activeUserModules++;
        if (mod.status_user === 'hidden' || mod.status_user === 'disabled') hiddenModules++;
        if (mod.visibility === 'everyone') everyoneVisibility++;
        if (mod.children) countModules(mod.children);
      });
    };
    
    countModules(modules);
    return { activeUserModules, hiddenModules, everyoneVisibility };
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleModule = (moduleKey) => {
    setExpandedModules(prev => 
      prev.includes(moduleKey) 
        ? prev.filter(k => k !== moduleKey)
        : [...prev, moduleKey]
    );
  };

  // Group modules by category
  const groupedModules = modules.reduce((acc, module) => {
    const category = module.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(module);
    return acc;
  }, {});

  const renderStatusBadge = (status, onClick, isUpdating) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.disabled;
    return (
      <Badge 
        className={`${config.color} ${isFullAdmin && !isUpdating ? 'cursor-pointer hover:opacity-80' : ''} transition-opacity`}
        onClick={isFullAdmin && !isUpdating ? onClick : undefined}
      >
        {isUpdating ? (
          <Loader2 className="w-3 h-3 animate-spin mr-1" />
        ) : (
          <span className={`w-2 h-2 rounded-full ${config.dotColor} mr-1`} />
        )}
        {config.label}
      </Badge>
    );
  };

  const renderToggleRow = (module, field, label, icon) => {
    const status = module[field] || 'coming_soon';
    const isActive = status === 'active';
    const isUpdating = updating === `${module.module_key}-${field}`;
    const Icon = icon;
    const isHideable = !NON_HIDEABLE_MODULES.includes(module.module_key) && 
                       !NON_HIDEABLE_MODULES.some(key => module.parent_key === key);
    
    // For admin status, use simple toggle
    if (field === 'status_admin') {
      return (
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">{label}</span>
          </div>
          <div className="flex items-center gap-3">
            {renderStatusBadge(
              status,
              () => handleStatusCycle(module.module_key, field, status),
              isUpdating
            )}
            {isUpdating ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            ) : (
              <Switch
                checked={isActive}
                onCheckedChange={() => handleToggle(module.module_key, field, status)}
                disabled={!isFullAdmin}
              />
            )}
          </div>
        </div>
      );
    }
    
    // For user status, use dropdown with Active/Coming Soon/Hidden options
    return (
      <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {isUpdating ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              <span className="text-sm text-gray-500">Updating...</span>
            </div>
          ) : (
            <Select
              value={status}
              onValueChange={(newStatus) => handleUserStatusChange(module.module_key, newStatus)}
              disabled={!isFullAdmin}
            >
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    {status === 'hidden' && <EyeOff className="w-3 h-3" />}
                    <span className={`w-2 h-2 rounded-full ${STATUS_CONFIG[status]?.dotColor || 'bg-gray-400'}`} />
                    <span className="text-sm">{STATUS_CONFIG[status]?.label || status}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Active
                  </div>
                </SelectItem>
                <SelectItem value="coming_soon">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500" />
                    Coming Soon
                  </div>
                </SelectItem>
                {isHideable && (
                  <SelectItem value="hidden">
                    <div className="flex items-center gap-2">
                      <EyeOff className="w-3 h-3 text-gray-500" />
                      Hidden
                    </div>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    );
  };

  const renderVisibilityRow = (module) => {
    const visibility = module.visibility || 'logged_in';
    const isUpdating = updating === `${module.module_key}-visibility`;
    const visibilityConfig = VISIBILITY_CONFIG[visibility] || VISIBILITY_CONFIG.logged_in;
    
    return (
      <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-blue-50/50 border border-blue-100">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">Visibility</span>
        </div>
        <div className="flex items-center gap-2">
          {isUpdating ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              <span className="text-sm text-gray-500">Updating...</span>
            </div>
          ) : (
            <Select
              value={visibility}
              onValueChange={(newVisibility) => handleVisibilityChange(module.module_key, newVisibility)}
              disabled={!isFullAdmin}
            >
              <SelectTrigger className="w-[160px] h-8">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{visibilityConfig.label}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">
                  <div className="flex flex-col">
                    <span className="font-medium">Everyone</span>
                    <span className="text-xs text-gray-500">Guests can see</span>
                  </div>
                </SelectItem>
                <SelectItem value="logged_in">
                  <div className="flex flex-col">
                    <span className="font-medium">Logged-in Only</span>
                    <span className="text-xs text-gray-500">Hidden from guests</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    );
  };

  const renderChildModule = (child) => {
    const Icon = ICON_MAP[child.icon] || FileText;
    const isOral = child.module_key.includes('_oral');
    
    return (
      <Card key={child.module_key} className="border-l-4 border-l-gray-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isOral ? 'bg-orange-100' : 'bg-blue-100'}`}>
              <Icon className={`w-4 h-4 ${isOral ? 'text-orange-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 text-sm">{child.display_name}</h4>
              <p className="text-xs text-gray-500">{child.description}</p>
            </div>
          </div>
          <div className="space-y-2">
            {/* Visibility is controlled at parent level only - not shown for children */}
            {renderToggleRow(child, 'status_admin', 'Admins', UserCog)}
            {renderToggleRow(child, 'status_user', 'Users', Users)}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderParentModule = (module) => {
    const Icon = ICON_MAP[module.icon] || Settings;
    const children = module.children || [];
    const isExpanded = expandedModules.includes(module.module_key);
    
    // Count active children for each type
    const adminActiveCount = children.filter(c => c.status_admin === 'active').length;
    const userActiveCount = children.filter(c => c.status_user === 'active').length;
    
    return (
      <Collapsible
        key={module.module_key}
        open={isExpanded}
        onOpenChange={() => toggleModule(module.module_key)}
      >
        <Card className="border-2">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0A7EA4]/20 to-[#14B8A6]/20 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#0A7EA4]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {module.display_name}
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </CardTitle>
                    <CardDescription>{module.description}</CardDescription>
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-right">
                  <div className="flex items-center gap-2 text-xs">
                    <UserCog className="w-3 h-3 text-purple-600" />
                    <span className="text-gray-600">Admins:</span>
                    <Badge variant="outline" className="text-xs">
                      {adminActiveCount}/{children.length} active
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Users className="w-3 h-3 text-blue-600" />
                    <span className="text-gray-600">Users:</span>
                    <Badge variant="outline" className="text-xs">
                      {userActiveCount}/{children.length} active
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {/* Child modules grid */}
              <div className="grid gap-3 md:grid-cols-2">
                {children.map(child => renderChildModule(child))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    );
  };

  const renderStandaloneModule = (module) => {
    const Icon = ICON_MAP[module.icon] || Settings;
    
    return (
      <Card key={module.module_key}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Icon className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{module.display_name}</h4>
              <p className="text-sm text-gray-500">{module.description}</p>
            </div>
          </div>
          <div className="space-y-2">
            {/* Visibility is controlled from the Guest Visibility Settings panel above */}
            {renderToggleRow(module, 'status_admin', 'Admins', UserCog)}
            {renderToggleRow(module, 'status_user', 'Users', Users)}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-gray-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Tools
          </Button>
          {isFullAdmin && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowResetConfirm(true)}
              disabled={resetting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Reset to Defaults
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 mb-6">
          <ToggleRight className="w-8 h-8 text-[#0A7EA4]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Module Activation</h1>
            <p className="text-gray-600">Control feature visibility for admins and users separately</p>
          </div>
        </div>

        {/* Legend */}
        <Card className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border-none">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <UserCog className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-900">Admins:</span>
                <span className="text-purple-700">Test & demo before launch</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-900">Users:</span>
                <span className="text-blue-700">Control user-facing visibility</span>
              </div>
              <div className="flex items-center gap-4 ml-auto">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-gray-600">Active</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span className="text-gray-600">Coming Soon</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-gray-600">Disabled</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Guest Visibility Settings - Collapsible */}
        <Collapsible
          open={visibilityExpanded}
          onOpenChange={setVisibilityExpanded}
          className="mb-6"
        >
          <Card className="border-2 border-blue-200 bg-blue-50/50">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-blue-100/50 transition-colors pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-200">
                      <Eye className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
                        Guest Visibility Settings
                        {visibilityExpanded ? (
                          <ChevronDown className="w-5 h-5 text-blue-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-blue-500" />
                        )}
                      </CardTitle>
                      <CardDescription className="text-blue-700">
                        Control what non-logged-in users see on the dashboard
                      </CardDescription>
                    </div>
                  </div>
                  {visibilityLoading && (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                {/* Visibility Options Grid */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {/* Oral Question Banks */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2">
                      <Mic className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-medium text-gray-700">Oral Question Banks</span>
                    </div>
                    <Select
                      value={visibilitySettings.oral || 'logged_in'}
                      onValueChange={(val) => setVisibilitySettings(prev => ({...prev, oral: val}))}
                      disabled={!isFullAdmin || visibilitySaving}
                    >
                      <SelectTrigger className="w-[140px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="logged_in">Logged-in Only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Written Question Banks */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2">
                      <PenTool className="w-4 h-4 text-pink-500" />
                      <span className="text-sm font-medium text-gray-700">Written Question Banks</span>
                    </div>
                    <Select
                      value={visibilitySettings.written || 'logged_in'}
                      onValueChange={(val) => setVisibilitySettings(prev => ({...prev, written: val}))}
                      disabled={!isFullAdmin || visibilitySaving}
                    >
                      <SelectTrigger className="w-[140px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="logged_in">Logged-in Only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Practice Sessions */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2">
                      <PlayCircle className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium text-gray-700">Practice Sessions</span>
                    </div>
                    <Select
                      value={visibilitySettings.practice_sessions || 'logged_in'}
                      onValueChange={(val) => setVisibilitySettings(prev => ({...prev, practice_sessions: val}))}
                      disabled={!isFullAdmin || visibilitySaving}
                    >
                      <SelectTrigger className="w-[140px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="logged_in">Logged-in Only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Progress Tracker */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-700">Progress Tracker</span>
                    </div>
                    <Select
                      value={visibilitySettings.progress_tracker || 'logged_in'}
                      onValueChange={(val) => setVisibilitySettings(prev => ({...prev, progress_tracker: val}))}
                      disabled={!isFullAdmin || visibilitySaving}
                    >
                      <SelectTrigger className="w-[140px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="logged_in">Logged-in Only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Study Notebook */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2">
                      <Notebook className="w-4 h-4 text-teal-500" />
                      <span className="text-sm font-medium text-gray-700">Study Notebook</span>
                    </div>
                    <Select
                      value={visibilitySettings.notebook || 'logged_in'}
                      onValueChange={(val) => setVisibilitySettings(prev => ({...prev, notebook: val}))}
                      disabled={!isFullAdmin || visibilitySaving}
                    >
                      <SelectTrigger className="w-[140px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="logged_in">Logged-in Only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Exam Insights */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-cyan-500" />
                      <span className="text-sm font-medium text-gray-700">Exam Insights</span>
                    </div>
                    <Select
                      value={visibilitySettings.exam_insights || 'logged_in'}
                      onValueChange={(val) => setVisibilitySettings(prev => ({...prev, exam_insights: val}))}
                      disabled={!isFullAdmin || visibilitySaving}
                    >
                      <SelectTrigger className="w-[140px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="logged_in">Logged-in Only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Live Classes (parent-level visibility; per-grade
                      rollout lives in the module_settings grade rows). */}
                  <div
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100"
                    data-testid="visibility-row-live-classes"
                  >
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-[#FF6B6B]" />
                      <span className="text-sm font-medium text-gray-700">Live Classes</span>
                    </div>
                    <Select
                      value={visibilitySettings.live_classes || 'logged_in'}
                      onValueChange={(val) => setVisibilitySettings(prev => ({...prev, live_classes: val}))}
                      disabled={!isFullAdmin || visibilitySaving}
                    >
                      <SelectTrigger className="w-[140px] h-8" data-testid="visibility-select-live-classes">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="logged_in">Logged-in Only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-4 text-xs text-gray-500 pt-2 border-t border-blue-100">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Everyone:</span> Visible to guests
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Logged-in Only:</span> Requires login
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Hidden:</span> Not shown
                  </div>
                </div>

                {/* Save Button */}
                {isFullAdmin && (
                  <Button
                    onClick={handleVisibilitySave}
                    disabled={visibilitySaving}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {visibilitySaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Visibility Settings'
                    )}
                  </Button>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {!isFullAdmin && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
            <p className="text-sm">Only full admins can modify module settings. You can view but not change.</p>
          </div>
        )}

        {/* Error State */}
        {fetchError && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ToggleRight className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Load Modules</h3>
              <p className="text-red-600 mb-4">{fetchError}</p>
              <Button 
                onClick={fetchModules} 
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#0A7EA4]" />
            <p className="text-gray-500 mt-2">Loading modules...</p>
          </div>
        ) : fetchError ? null : (
          <div className="space-y-8">
            {CATEGORY_ORDER.map(category => {
              const categoryModules = groupedModules[category] || [];
              if (categoryModules.length === 0) return null;
              
              const parentModules = categoryModules.filter(m => m.children && m.children.length > 0);
              const standaloneModules = categoryModules.filter(m => !m.children || m.children.length === 0);
              
              return (
                <Collapsible
                  key={category}
                  open={expandedCategories.includes(category)}
                  onOpenChange={() => toggleCategory(category)}
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center gap-2 cursor-pointer group mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {CATEGORY_LABELS[category] || category}
                      </h2>
                      <Badge variant="outline" className="text-xs">
                        {categoryModules.length} {categoryModules.length === 1 ? 'module' : 'modules'}
                      </Badge>
                      {expandedCategories.includes(category) ? (
                        <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-4">
                      {parentModules.map(module => renderParentModule(module))}
                      {standaloneModules.length > 0 && (
                        <div className="grid gap-4 md:grid-cols-2">
                          {standaloneModules.map(module => renderStandaloneModule(module))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        )}
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Reset All Modules?
            </DialogTitle>
            <DialogDescription className="text-left">
              This will reset all module settings to their default configuration.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current State Summary */}
            {(() => {
              const impact = getResetImpact();
              return (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700">Current Configuration:</div>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Modules with User Status &quot;Active&quot;:</span>
                      <span className="font-medium">{impact.activeUserModules}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Modules with Guest Visibility &quot;Everyone&quot;:</span>
                      <span className="font-medium">{impact.everyoneVisibility}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* What will happen */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700">After Reset:</div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>All <strong>User Status</strong> will be set to <Badge className="bg-yellow-100 text-yellow-800 text-xs ml-1">Coming Soon</Badge></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>All <strong>Guest Visibility</strong> will be set to <Badge className="bg-purple-100 text-purple-800 text-xs ml-1">Logged-in Only</Badge></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>All <strong>Admin Status</strong> will remain <Badge className="bg-green-100 text-green-800 text-xs ml-1">Active</Badge></span>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700 font-medium">
                ⚠️ This action cannot be undone. You will need to manually reconfigure each module.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReset}
              disabled={resetting}
            >
              {resetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Yes, Reset All'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ModuleActivationPage;
